import {
  Checkbox,
  Flex,
  Image,
  Text,
  FlexProps,
  Box,
  Badge,
  Tooltip,
} from "@chakra-ui/react";
import { getAssetShortName } from "@syla/shared/helpers/assets/getAssetShortName";
import { formatStringWithCommas } from "@syla/shared/helpers/formatting";
import {
  includeNeedsReviewItem,
  TransactionSourceIdType,
} from "@syla/shared/types/models/TransactionBase";
import { uniq } from "lodash";
import React, { memo, useMemo } from "react";
import { AccountTimeZone } from "../../../../constants/AccountTimeZone";
import { BalanceDetail } from "../../../../helper/ledger/aggregateBalanceInLedgers";
import { NumberStyler } from "../../../../helper/NumberStyler";
import { getOrderIconAndText } from "../../../../helper/transaction/getTransactionIconAndText";
import { formatDateLocal } from "../../../../helpers/formatDateLocal";
import { useTransactionValues } from "../../../../hooks/useTransactionValues";
import { useTransactionsStore } from "../../../../store/transactionsStore";
import { ImageWithMissingSrc } from "../../../atoms/ImageWithMissingSrc";
import { SkeletonRect } from "../../../atoms/Skeletons";
import { AddressDisplay } from "./AddressDisplay";
import { OrderNeedsReviewSummary } from "./OrderNeedsReviewSummary";
import { useGetTxHint } from "./useGetTxHint";

type MainTransactionTableRowProps = {
  groupId: string;
  onSelect: (groupId: string) => void;
  isSelected: boolean;
  onClickRow: (transactionId: string) => void;

  // for running balance
  runningBalanceAssetId?: string;
  useWalletBalance?: boolean;
};

export const MainTransactionTableRow = memo(
  ({
    groupId,
    onSelect,
    isSelected,
    onClickRow,
    runningBalanceAssetId,
    useWalletBalance,
  }: MainTransactionTableRowProps): JSX.Element => {
    // The checkbox depends on onSelect, which is not stable when the list of
    // displayed groups changes or when select all is toggled on/off. To avoid
    // expensive re-renders, we memo the majority of the row which is not
    // dependent on onSelect

    return (
      <Box display="contents" role="group">
        <TableCell
          isSelected={isSelected}
          position="sticky"
          left={0}
          zIndex={1}
          borderRightWidth="1px"
          borderTopLeftRadius="md"
          borderBottomLeftRadius="md"
        >
          <Checkbox
            size="lg"
            isChecked={isSelected}
            colorScheme="primary"
            justifyContent="center"
            backgroundColor="white"
            onChange={() => {
              onSelect(groupId);
            }}
          />
        </TableCell>

        <MainTransactionTableRowContent
          groupId={groupId}
          onClickRow={onClickRow}
          runningBalanceAssetId={runningBalanceAssetId}
          useWalletBalance={useWalletBalance}
          isSelected={isSelected}
        />
      </Box>
    );
  },
);

type MainTransactionTableRowContentProps = {
  groupId: string;
  onClickRow: (transactionId: string) => void;
  runningBalanceAssetId?: string;
  useWalletBalance?: boolean;
  isSelected: boolean;
};

const MainTransactionTableRowContent = memo(
  function MainTransactionTableRowContent({
    groupId,
    onClickRow,
    runningBalanceAssetId,
    useWalletBalance,
    isSelected,
  }: MainTransactionTableRowContentProps): React.ReactElement {
    const orderResponse = useTransactionsStore(
      (state) => state.groups?.[groupId]?.data,
    );

    if (!orderResponse) {
      throw new Error("Group should be set");
    }

    const transactionId = orderResponse.transactions[0]?._id;

    const { negativeChanges, positiveChanges, outSources, inSources } =
      useTransactionValues(orderResponse.transactions);

    const showNeedsReviewIndicator = orderResponse.transactions.some(
      (transaction) => transaction.needsReview?.some(includeNeedsReviewItem),
    );

    const { icon, text } = getOrderIconAndText(orderResponse);

    const hint = useGetTxHint(orderResponse);

    const { fromAddresses, toAddresses } = useMemo(() => {
      const from: string[] = [];
      const to: string[] = [];

      for (const transaction of orderResponse.transactions) {
        for (const sourceId of transaction.sourceIds ?? []) {
          if (sourceId.type === TransactionSourceIdType.ReceivedFromAddress) {
            from.push(sourceId.id);
          }

          if (sourceId.type === TransactionSourceIdType.SentToAddress) {
            to.push(sourceId.id);
          }
        }
      }

      return {
        fromAddresses: uniq(from),
        toAddresses: uniq(to),
      };
    }, [orderResponse.transactions]);

    const showRunningBalance = runningBalanceAssetId !== undefined;

    const runningBalance = useMemo(() => {
      if (runningBalanceAssetId === undefined) {
        return undefined;
      }

      const balanceKey = useWalletBalance ? "walletBalance" : "balance";

      for (let i = orderResponse.transactions.length - 1; i >= 0; i--) {
        const transaction = orderResponse.transactions[i];

        for (let j = transaction.ledgers.length - 1; j >= 0; j--) {
          const ledger = transaction.ledgers[j];

          if (ledger.asset._id === runningBalanceAssetId) {
            return ledger.balances?.[balanceKey];
          }
        }
      }

      return undefined;
    }, [orderResponse.transactions, runningBalanceAssetId, useWalletBalance]);

    const dataSourceName = (outSources.length > 0 ? outSources : inSources)[0];

    return (
      <>
        <TableCell
          isSelected={isSelected}
          onClick={() => onClickRow(transactionId)}
        >
          <Flex direction="column" align="flex-start">
            <Text fontSize="sm" color="gray.800">
              {formatDateLocal(orderResponse.order.date, {
                timeZone: AccountTimeZone,
                dateStyle: "medium",
              })}
            </Text>
            <Text fontSize="sm" fontWeight="light" color="gray.600">
              {formatDateLocal(orderResponse.order.date, {
                timeZone: AccountTimeZone,
                timeStyle: "medium",
              })}
            </Text>
          </Flex>
        </TableCell>
        <TableCell
          isSelected={isSelected}
          onClick={() => onClickRow(transactionId)}
        >
          <Tooltip label={dataSourceName}>
            <Flex gap={1} align="center">
              <ImageWithMissingSrc
                src={orderResponse.transactions[0].dataSource.image}
                alt=""
                w="1.4rem"
                h="1.4rem"
                mr={1}
              />

              <Text fontSize="sm" color="gray.800" noOfLines={2} minW={0}>
                {dataSourceName}
              </Text>
            </Flex>
          </Tooltip>
        </TableCell>
        <TableCell
          isSelected={isSelected}
          onClick={() => onClickRow(transactionId)}
        >
          <Image src={icon} alt="" mr="0.5rem" boxSize="2rem" />
          <Box>
            <Text whiteSpace="nowrap" fontSize="sm">
              {text}
            </Text>
            {!!hint && hint !== text && (
              <Text
                color="gray.600"
                fontSize="xs"
                fontWeight="light"
                noOfLines={1}
                maxW="8em"
              >
                {hint}
              </Text>
            )}
          </Box>
        </TableCell>
        <MovementCell
          isSelected={isSelected}
          changes={negativeChanges}
          counterPartyAddresses={fromAddresses}
          onClick={() => onClickRow(transactionId)}
        />
        <MovementCell
          isSelected={isSelected}
          changes={positiveChanges}
          counterPartyAddresses={toAddresses}
          onClick={() => onClickRow(transactionId)}
        />
        {showRunningBalance && (
          <TableCell
            isSelected={isSelected}
            onClick={() => onClickRow(transactionId)}
          >
            {orderResponse && (
              <SkeletonRect isLoaded={true}>
                <NumberStyler num={runningBalance} negativeColored />
              </SkeletonRect>
            )}
          </TableCell>
        )}
        <TableCell
          isSelected={isSelected}
          justifyContent="flex-end"
          onClick={() => onClickRow(transactionId)}
        >
          {/* We display no value for an undefined tax outcome, in contrast to a
          zero value for tax outcomes that sum to zero.*/}
          {orderResponse.taxOutcome && (
            <SkeletonRect isLoaded={true}>
              <Badge
                px={2}
                colorScheme={
                  orderResponse.taxOutcome.isZeroOrPositive() ? "green" : "red"
                }
              >
                <NumberStyler
                  num={orderResponse.taxOutcome}
                  unit="currency"
                  fontWeight="semibold"
                />
              </Badge>
            </SkeletonRect>
          )}
        </TableCell>
        <TableCell
          isSelected={isSelected}
          onClick={() => onClickRow(transactionId)}
          borderTopRightRadius="md"
          borderBottomRightRadius="md"
        >
          {showNeedsReviewIndicator && (
            <OrderNeedsReviewSummary
              transactions={orderResponse.transactions}
            />
          )}
        </TableCell>
      </>
    );
  },
);

function MovementCell({
  changes,
  onClick,
  counterPartyAddresses,
  isSelected,
}: {
  changes: BalanceDetail[];
  onClick: () => void;
  counterPartyAddresses: string[];
  isSelected: boolean;
}): React.ReactElement {
  const maxDisplay = 10;

  const displayChanges = changes.slice(0, maxDisplay);

  const hasMore = changes.length > maxDisplay;

  if (changes.length === 0 && counterPartyAddresses.length > 0) {
    return (
      <TableCell isSelected={isSelected} onClick={onClick}>
        <AddressDisplay addresses={counterPartyAddresses} />
      </TableCell>
    );
  }

  return (
    <TableCell isSelected={isSelected} onClick={onClick} gap={2}>
      <Flex direction="column" justify="flex-start">
        {displayChanges.map((change, index) => {
          const isPositive = change.amount.isZeroOrPositive();

          const assetShortName = getAssetShortName(change.asset);

          return (
            <Flex key={index}>
              <Tooltip shouldWrapChildren={true} label={assetShortName}>
                <ImageWithMissingSrc
                  src={change.asset.image}
                  alt=""
                  w="1.5rem"
                  h="1.5rem"
                  mr={1}
                />
              </Tooltip>

              <Text color={isPositive ? "green.500" : "red.500"} mr="0.5ch">
                {isPositive ? "+" : "-"}
              </Text>
              <Text mr="0.5ch">
                {formatStringWithCommas(
                  change.amount.abs().toSignificantDigits(8).toString(),
                )}
              </Text>
              <Text fontSize="md" noOfLines={1}>
                {assetShortName}
              </Text>
            </Flex>
          );
        })}
        {hasMore && (
          <Text fontSize="xs" color="gray.700">
            + {changes.length - maxDisplay} more
          </Text>
        )}
      </Flex>
    </TableCell>
  );
}

function TableCell({
  isSelected,
  ...props
}: { isSelected: boolean } & FlexProps): React.ReactElement {
  return (
    <Flex
      minW={0}
      direction="row"
      px={3}
      py={2}
      backgroundColor={isSelected ? "primary.25" : "white"}
      _groupHover={{
        backgroundColor: isSelected ? "primary.50" : "gray.100",
      }}
      align="center"
      cursor="pointer"
      {...props}
    />
  );
}
